/**
 * Detecta e normaliza contato compartilhado (JSON legado, vCard, payload WhatsApp em json_whatsapp).
 */
import { toE164Digits } from "./phone";

function pickNonEmpty(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    const s = typeof v === "string" ? v.trim() : String(v).trim();
    if (s) return s;
  }
  return "";
}

function digitsOnly(s) {
  if (s == null) return "";
  return String(s).replace(/\D/g, "");
}

function tryParseJson(text) {
  const t = text != null ? String(text).trim() : "";
  if (!t.startsWith("{") && !t.startsWith("[")) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function normalizePlainObject(o) {
  if (!o || typeof o !== "object" || Array.isArray(o)) return null;
  const nameObj = o.name && typeof o.name === "object" && !Array.isArray(o.name) ? o.name : null;
  let displayName = pickNonEmpty(
    o.nome,
    nameObj ? nameObj.formatted_name : "",
    nameObj ? nameObj.first_name : "",
    typeof o.name === "string" ? o.name : "",
    o.fn,
    o.formatted_name,
    o.formattedName,
    o.displayName,
    o.display_name
  );
  let phoneDisplay = pickNonEmpty(o.telefone, o.phone, o.celular, o.mobile, o.tel);
  let waDigits = o.whatsapp_id != null ? digitsOnly(o.whatsapp_id) : "";
  if (Array.isArray(o.phones) && o.phones[0] && typeof o.phones[0] === "object") {
    const ph = o.phones[0];
    if (!phoneDisplay) phoneDisplay = pickNonEmpty(ph.phone, ph.wa_id);
    if (!waDigits) waDigits = digitsOnly(ph.wa_id || ph.phone);
  }
  if (!waDigits && phoneDisplay) waDigits = digitsOnly(phoneDisplay);
  let email = null;
  if (o.email != null && String(o.email).trim()) email = String(o.email).trim();
  if (Array.isArray(o.emails) && o.emails[0] && o.emails[0].email != null) {
    const em = String(o.emails[0].email).trim();
    if (em) email = em;
  }
  if (!phoneDisplay && !waDigits) return null;
  if (!displayName) displayName = phoneDisplay || waDigits || "Contato";
  const whatsappE164 = waDigits ? toE164Digits(waDigits, "BR") || waDigits : "";
  return {
    displayName,
    phoneDisplay: phoneDisplay || (waDigits ? `+${waDigits}` : ""),
    whatsappDigits: waDigits,
    whatsappE164,
    email,
  };
}

function parseJsonContactText(text) {
  const parsed = tryParseJson(text);
  if (!parsed) return null;
  if (Array.isArray(parsed) && parsed[0] && typeof parsed[0] === "object") {
    return normalizePlainObject(parsed[0]);
  }
  return normalizePlainObject(parsed);
}

function parseVcardText(text) {
  const t = text != null ? String(text) : "";
  if (!/BEGIN:\s*VCARD/i.test(t)) return null;
  const fnM = t.match(/^\s*FN\s*:\s*(.+)$/im);
  const displayName = fnM ? fnM[1].trim().replace(/\\n/g, " ").replace(/\\,/g, ",") : "";
  const telM = t.match(/^\s*TEL[^:]*:\s*(.+)$/im);
  let rawTel = telM ? telM[1].trim() : "";
  rawTel = rawTel.replace(/^tel:/i, "").replace(/;type=[^;:]*/gi, "");
  const phoneDisplay = rawTel.replace(/\s+/g, " ").trim();
  const waDigits = digitsOnly(phoneDisplay);
  const emailM = t.match(/^\s*EMAIL[^:]*:\s*(.+)$/im);
  const email = emailM ? emailM[1].trim().split(";")[0].trim() : null;
  if (!phoneDisplay && !waDigits) return null;
  const whatsappE164 = waDigits ? toE164Digits(waDigits, "BR") || waDigits : "";
  return {
    displayName: displayName || phoneDisplay || waDigits || "Contato",
    phoneDisplay: phoneDisplay || (waDigits ? `+${waDigits}` : ""),
    whatsappDigits: waDigits,
    whatsappE164,
    email: email || null,
  };
}

function parseJsonWhatsapp(jsonWhatsappRaw) {
  if (jsonWhatsappRaw == null) return null;
  let j = jsonWhatsappRaw;
  if (typeof j === "string") {
    try {
      j = JSON.parse(j);
    } catch {
      return null;
    }
  }
  if (!j || typeof j !== "object") return null;
  const m0 =
    j.messages && Array.isArray(j.messages) && j.messages[0] && typeof j.messages[0] === "object"
      ? j.messages[0]
      : null;
  if (m0 && m0.type === "contacts" && Array.isArray(m0.contacts)) {
    const hit = normalizePlainObject(m0.contacts[0]);
    if (hit) return hit;
  }
  const entry0 = j.entry && j.entry[0];
  const ch0 = entry0 && entry0.changes && entry0.changes[0];
  const val = ch0 && ch0.value;
  const wm = val && val.messages && val.messages[0];
  if (wm && wm.type === "contacts" && Array.isArray(wm.contacts)) {
    const hit = normalizePlainObject(wm.contacts[0]);
    if (hit) return hit;
  }
  if (j.payload && typeof j.payload === "object") {
    const p = j.payload;
    const inner = p.payload && typeof p.payload === "object" ? p.payload : p;
    const mt = String(p.type || inner.type || "").toLowerCase();
    if (mt === "contact" || mt === "contacts") {
      const contacts = inner.contacts || inner.contact || p.contacts;
      if (Array.isArray(contacts) && contacts[0]) {
        const hit = normalizePlainObject(contacts[0]);
        if (hit) return hit;
      }
    }
  }
  return null;
}

/**
 * @param {string|null|undefined} text
 * @param {string|object|null|undefined} jsonWhatsappRaw
 * @returns {{ displayName: string, phoneDisplay: string, whatsappDigits: string, email: string|null }|null}
 */
export function parseSharedContactPayload(text, jsonWhatsappRaw) {
  const fromText = parseJsonContactText(text) || parseVcardText(text);
  if (fromText) return fromText;
  return parseJsonWhatsapp(jsonWhatsappRaw);
}

/**
 * @param {object|null|undefined} message
 */
export function parseSharedContactFromMessage(message) {
  if (!message) return null;
  const c = message.contact;
  if (c && typeof c === "object") {
    const displayName = pickNonEmpty(c.name, c.nome);
    const phoneDisplay = pickNonEmpty(c.phone, c.telefone);
    let waDigits = c.whatsappId != null ? digitsOnly(c.whatsappId) : "";
    if (!waDigits && c.whatsapp_id != null) waDigits = digitsOnly(c.whatsapp_id);
    if (!waDigits && phoneDisplay) waDigits = digitsOnly(phoneDisplay);
    const email = c.email != null && String(c.email).trim() ? String(c.email).trim() : null;
    if (!displayName && !phoneDisplay && !waDigits) return null;
    const whatsappE164 = waDigits ? toE164Digits(waDigits, "BR") || waDigits : "";
    return {
      displayName: displayName || phoneDisplay || waDigits || "Contato",
      phoneDisplay: phoneDisplay || (waDigits ? `+${waDigits}` : ""),
      whatsappDigits: waDigits,
      whatsappE164,
      email,
    };
  }
  return parseSharedContactPayload(message.text, message.json_whatsapp);
}
